import { useCallback, useMemo } from "react";
import {
  useLocation,
  useRouter,
  type HistoryState,
} from "@tanstack/react-router";
import { hrefWithQuery } from "~/router-search";

type SearchParamsUpdate =
  | URLSearchParams
  | ((current: URLSearchParams) => URLSearchParams);

interface SetSearchParamsOptions {
  replace?: boolean;
  state?: HistoryState;
}

/**
 * The query string as `URLSearchParams`, for pages that build URLs by hand.
 * Navigates with a raw `href`, so query strings keep their existing format.
 */
export function useSearchParams() {
  const router = useRouter();
  const { searchStr } = useLocation();

  const searchParams = useMemo(
    () => new URLSearchParams(searchStr),
    [searchStr],
  );

  const setSearchParams = useCallback(
    (update: SearchParamsUpdate, options: SetSearchParamsOptions = {}) => {
      // Updaters see the URL at call time, not the last render's.
      const { pathname, searchStr: currentSearch } = router.state.location;
      const next =
        typeof update === "function"
          ? update(new URLSearchParams(currentSearch))
          : update;

      return router.navigate({
        href: hrefWithQuery(pathname, next.toString()),
        replace: options.replace,
        state: options.state,
        resetScroll: false,
      });
    },
    [router],
  );

  return [searchParams, setSearchParams] as const;
}
